import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import { Upload, Table2, Wrench, History, Download, Undo2, Plus, Loader2, Moon, Sun, BarChart3, FolderOpen, BookMarked, ClipboardList } from 'lucide-react';
import FileUpload from './components/FileUpload';
import DataTable from './components/DataTable';
import OperationSelector from './components/OperationSelector';
import OperationHistory from './components/OperationHistory';
import ExportButtons from './components/ExportButtons';
import ColumnStatistics from './components/ColumnStatistics';
import ProjectRecord from './components/ProjectRecord';
import SavedFiles from './components/SavedFiles';
import Recipes from './components/Recipes';
import LanguageToggle from './components/LanguageToggle';
import { useT } from './i18n';
import { getPreview, getInfo, getHistory, undoOperation } from './utils/api';
import './App.css';

const STEPS = ['upload', 'preview', 'transform', 'history', 'project', 'export'];

function App() {
  const t = useT();
  const [currentStep, setCurrentStep] = useState('upload');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dataInfo, setDataInfo] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [operationHistory, setOperationHistory] = useState([]);
  const [showSavedFilesModal, setShowSavedFilesModal] = useState(false);
  const [showRecipesModal, setShowRecipesModal] = useState(false);
  const [showOperationModal, setShowOperationModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [maxUndo, setMaxUndo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    // An explicit choice wins over the OS preference. The old `||` could not
    // tell "never chosen" from "chosen off", so turning dark mode off and
    // reloading turned it straight back on.
    const stored = localStorage.getItem('darkMode');
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Apply dark mode class
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  const handleSessionLost = useCallback(() => {
    // The session expires after an hour. Without this the interface kept
    // showing data the server had already forgotten.
    setDataLoaded(false);
    setPreviewData(null);
    setDataInfo(null);
    setOperationHistory([]);
    setCanUndo(false);
    setCurrentStep('upload');
    toast.error(t('session.expired'));
    // `t` is a dependency on purpose: without it the callback would keep the
    // translator captured at mount and go on speaking the previous language
    // after a switch.
  }, [t]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [previewRes, infoRes, historyRes] = await Promise.all([
        getPreview(1, 50),
        getInfo(),
        getHistory(),
      ]);
      
      setPreviewData(previewRes.preview);
      setDataInfo(infoRes.info);
      const entries = historyRes.entries || (historyRes.history || []);
      setOperationHistory(entries);
      setCanUndo(historyRes.can_undo || false);
      // Le nombre vient du stockage qui l'applique, pas d'une
      // constante dans l'interface : c'est une variable
      // d'environnement côté MongoDB.
      setMaxUndo(historyRes.max_undo ?? null);
      return entries;
    } catch (error) {
      // This used to fail silently, so a failed refresh left stale data on
      // screen behind a green success toast.
      console.error('Error loading data:', error);
      if (error.response?.status === 400) {
        handleSessionLost();
      } else {
        toast.error(
          error.response?.data?.detail || t('session.refreshFailed')
        );
      }
    } finally {
      setLoading(false);
    }
  }, [handleSessionLost, t]);

  const handleUploadSuccess = (data) => {
    setPreviewData(data.preview);
    setDataInfo(data.info);
    setOperationHistory([
      { label: t('upload.rowsLoaded', { count: data.info.rows?.toLocaleString() }) },
    ]);
    setCanUndo(false);
    setDataLoaded(true);
    setCurrentStep('preview');
    toast.success(t('upload.success'));
    // Rows dropped or a separator guess are reported by the parser; showing
    // them is the difference between losing data and knowing you did.
    (data.warnings || []).forEach((warning) => toast.warning(warning, { duration: 8000 }));
  };

  const handleOperationApplied = async (result) => {
    setShowOperationModal(false);
    const entries = await loadData();
    // What the operation just did, said in the reader's language: the server
    // sends a code and its parameters, and the last history line is exactly
    // the operation that was applied. The English sentence it also sends is
    // the fallback for anything recorded before codes existed.
    const last = Array.isArray(entries) ? entries[entries.length - 1] : null;
    const written = last?.code ? t(last.code, last.params) : null;
    toast.success(
      result.localizedMessage || written || result.message || t('transform.applied')
    );
  };

  const handleUndo = useCallback(async () => {
    try {
      setLoading(true);
      const result = await undoOperation();
      setPreviewData(result.preview);
      setDataInfo(result.info);
      toast.success(t('history.undone'));
      // Refresh canUndo and the history list, which used to go stale here and
      // left the Undo button enabled on an empty stack.
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('history.undoFailed'));
    } finally {
      setLoading(false);
    }
  }, [loadData, t]);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    // Ctrl/Cmd + Z for undo
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && canUndo && !loading) {
      e.preventDefault();
      handleUndo();
    }
    // Escape to close modals
    if (e.key === 'Escape') {
      setShowOperationModal(false);
      setShowStatsModal(false);
    }
  }, [canUndo, loading, handleUndo]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (dataLoaded && (currentStep === 'preview' || currentStep === 'history' || currentStep === 'export')) {
      loadData();
    }
  }, [currentStep, dataLoaded, loadData]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="app-container">
      <Toaster position="top-center" richColors theme={darkMode ? 'dark' : 'light'} />
      
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/80 backdrop-blur border-b border-border" data-testid="app-header">
        <div className="max-w-[1400px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Sekoia Logo */}
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#1B5E3A] to-[#6BBF59] flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-xl">S</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
                  Excel Maestro
                  <span className="text-xs font-normal px-2 py-0.5 bg-primary/10 text-primary rounded-full">{t('app.byline')}</span>
                </h1>
                <p className="text-sm text-muted-foreground">{t('app.tagline')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageToggle />

              {/* Dark mode toggle */}
              <button
                onClick={toggleDarkMode}
                className="btn-ghost h-10 w-10 p-0 flex items-center justify-center theme-toggle"
                data-testid="dark-mode-toggle"
                aria-label={t('app.darkMode')}
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
              
              {/* Saved Files - always accessible */}
              <button
                onClick={() => setShowSavedFilesModal(true)}
                className="btn-secondary flex items-center gap-2"
                data-testid="saved-files-button"
              >
                <FolderOpen className="h-4 w-4" />
                {t('app.savedFiles')}
              </button>

              {/* Recipes - always accessible: the shelf outlives the session */}
              <button
                onClick={() => setShowRecipesModal(true)}
                className="btn-secondary flex items-center gap-2"
                data-testid="recipes-button"
              >
                <BookMarked className="h-4 w-4" />
                {t('app.recipes')}
              </button>
              
              {dataLoaded && (
                <>
                  <button
                    onClick={() => setShowStatsModal(true)}
                    className="btn-secondary flex items-center gap-2"
                    data-testid="stats-button"
                  >
                    <BarChart3 className="h-4 w-4" />
                    {t('app.statistics')}
                  </button>
                  <button
                    onClick={handleUndo}
                    disabled={!canUndo || loading}
                    className="btn-secondary flex items-center gap-2"
                    data-testid="undo-button"
                    title="Ctrl+Z"
                  >
                    <Undo2 className="h-4 w-4" />
                    {t('app.undo')}
                  </button>
                  <ExportButtons />
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Progress bar */}
        {loading && (
          <div className="h-1 w-full bg-muted overflow-hidden">
            <div className="h-full w-1/4 bg-primary progress-indeterminate"></div>
          </div>
        )}
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-8">
          {/* Sidebar */}
          <aside className="space-y-4">
            <div className="card p-4 slide-up">
              <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-primary"></div>
                {t('steps.title')}
              </h2>
            <div className="space-y-1" role="navigation">
                {STEPS.map((step, idx) => {
                  const isActive = currentStep === step;
                  // An step is clickable if data is loaded (for all steps except upload)
                  // or if it's the upload step
                  const isClickable = dataLoaded || step === 'upload';
                  // A step is "completed" (shows checkmark) if we've moved past it
                  const isCompleted = STEPS.indexOf(currentStep) > idx || (step === 'upload' && dataLoaded);
                  const icons = {
                    upload: Upload,
                    preview: Table2,
                    transform: Wrench,
                    history: History,
                    project: ClipboardList,
                    export: Download,
                  };
                  const labels = {
                    upload: t('steps.upload'),
                    preview: t('steps.preview'),
                    transform: t('steps.transform'),
                    history: t('steps.history'),
                    project: t('steps.project'),
                    export: t('steps.export'),
                  };
                  const Icon = icons[step];
                  
                  return (
                    <button
                      key={step}
                      onClick={() => isClickable && setCurrentStep(step)}
                      disabled={!isClickable}
                      data-current={isActive}
                      data-testid={`step-${step}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-200 ${
                        isActive 
                          ? 'bg-primary/10 text-primary font-medium step-active' 
                          : isClickable 
                            ? 'text-foreground hover:bg-muted cursor-pointer' 
                            : 'text-muted-foreground opacity-60 cursor-default pointer-events-none'
                      }`}
                    >
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                        isActive 
                          ? 'bg-primary text-primary-foreground' 
                          : isClickable 
                            ? 'bg-muted' 
                            : 'bg-muted/50'
                      }`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="text-sm">{labels[step]}</span>
                      {isCompleted && !isActive && (
                        <div className="ml-auto h-5 w-5 rounded-full bg-success/20 text-success text-xs flex items-center justify-center">✓</div>
                      )}
                    </button>
                  );
                })}
            </div>
            </div>

            {dataInfo && (
              <div className="card p-4 slide-up" style={{ animationDelay: '100ms' }}>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-accent"></div>
                  {t('summary.title')}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t('summary.rows')}</span>
                    <span className="text-sm font-semibold badge badge-primary">{dataInfo.rows?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t('summary.columns')}</span>
                    <span className="text-sm font-semibold badge badge-accent">{dataInfo.columns}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t('summary.size')}</span>
                    <span className="text-sm font-medium">{dataInfo.memory_usage_mb?.toFixed(2)} MB</span>
                  </div>
                </div>
                
                {/* Column types preview */}
                {dataInfo.column_types && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <h4 className="text-xs font-medium text-muted-foreground mb-2">{t('summary.columnTypes')}</h4>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(
                        dataInfo.column_types.reduce((acc, type) => {
                          acc[type] = (acc[type] || 0) + 1;
                          return acc;
                        }, {})
                      ).map(([type, count]) => (
                        <span key={type} className="badge badge-primary text-xs">
                          {type}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Keyboard shortcuts hint */}
            <div className="card p-4 slide-up" style={{ animationDelay: '200ms' }}>
              <h3 className="text-xs font-semibold text-muted-foreground mb-2">{t('shortcuts.title')}</h3>
              <div className="space-y-1 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span>{t('shortcuts.undo')}</span>
                  <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Ctrl+Z</kbd>
                </div>
                <div className="flex justify-between">
                  <span>{t('shortcuts.closeModal')}</span>
                  <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Esc</kbd>
                </div>
              </div>
            </div>
          </aside>

          {/* Main content */}
          <div className="space-y-6">
            {currentStep === 'upload' && (
              <div className="card p-8 slide-up">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 mb-4">
                    <Upload className="h-8 w-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">{t('upload.title')}</h2>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    {/* Le compte de lignes annonce ici etait faux : « up to 150,000 rows »
                        alors que la mesure donne ~2 millions sur 12 colonnes, et que la
                        vraie borne est la taille du fichier, pas son nombre de lignes. */}
                    {t('upload.subtitle')}
                  </p>
                </div>
                <FileUpload onSuccess={handleUploadSuccess} />
              </div>
            )}

            {currentStep === 'preview' && previewData && (
              <div className="space-y-4 slide-up">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">{t('preview.title')}</h2>
                    <p className="text-sm text-muted-foreground">{t('preview.subtitle')}</p>
                  </div>
                  <button
                    onClick={() => setShowOperationModal(true)}
                    className="btn-primary flex items-center gap-2"
                    data-testid="new-operation-button"
                  >
                    <Plus className="h-4 w-4" />
                    {t('preview.newOperation')}
                  </button>
                </div>
                <div className="card p-6">
                  <DataTable data={previewData} loading={loading} />
                </div>
              </div>
            )}

            {currentStep === 'transform' && (
              <div className="space-y-4 slide-up">
                <div>
                  <h2 className="text-xl font-semibold">{t('transform.title')}</h2>
                  <p className="text-sm text-muted-foreground">{t('transform.subtitle')}</p>
                </div>
                <div className="card p-8 text-center">
                  <div className="max-w-md mx-auto">
                    <div className="inline-flex items-center justify-center h-20 w-20 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 mb-6">
                      <Wrench className="h-10 w-10 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{t('transform.headline')}</h3>
                    <p className="text-muted-foreground mb-6">
                      {t('transform.body')}
                    </p>
                    <button
                      onClick={() => setShowOperationModal(true)}
                      className="btn-primary flex items-center gap-2 mx-auto"
                      data-testid="select-operation-button"
                    >
                      <Plus className="h-4 w-4" />
                      {t('transform.select')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 'project' && (
              <div className="space-y-4 slide-up">
                <ProjectRecord />
              </div>
            )}

            {currentStep === 'history' && (
              <div className="space-y-4 slide-up">
                <div>
                  <h2 className="text-xl font-semibold">{t('history.title')}</h2>
                  <p className="text-sm text-muted-foreground">{t('history.subtitle')}</p>
                </div>
                <div className="card p-6">
                  <OperationHistory 
                    history={operationHistory} 
                    onUndo={handleUndo}
                    canUndo={canUndo}
                    maxUndo={maxUndo}
                  />
                </div>
              </div>
            )}

            {currentStep === 'export' && previewData && (
              <div className="space-y-4 slide-up">
                <div>
                  <h2 className="text-xl font-semibold">{t('export.title')}</h2>
                  <p className="text-sm text-muted-foreground">{t('export.subtitle')}</p>
                </div>
                <div className="card p-8">
                  <div className="max-w-2xl mx-auto text-center">
                    <div className="inline-flex items-center justify-center h-20 w-20 rounded-2xl bg-gradient-to-br from-success/20 to-accent/20 mb-6">
                      <Download className="h-10 w-10 text-success" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{t('export.headline')}</h3>
                    <p className="text-muted-foreground mb-6">
                      {t('export.body')}
                    </p>
                    <ExportButtons size="lg" />
                  </div>
                  
                  <div className="mt-8 pt-8 border-t border-border">
                    <h4 className="text-sm font-semibold mb-4">{t('export.finalPreview')}</h4>
                    <DataTable data={previewData} loading={loading} compact />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Operation Modal */}
      {showOperationModal && (
        <OperationSelector
          onClose={() => setShowOperationModal(false)}
          onOperationApplied={handleOperationApplied}
        />
      )}
      
      {/* Statistics Modal */}
      {showStatsModal && (
        <ColumnStatistics
          onClose={() => setShowStatsModal(false)}
        />
      )}

      {/* Saved Files Modal */}
      {showSavedFilesModal && (
        <SavedFiles
          onClose={() => setShowSavedFilesModal(false)}
          onFileLoaded={(result) => {
            setPreviewData(result.preview);
            setDataInfo(result.info);
            setDataLoaded(true);
            setCurrentStep('preview');
            loadData();
          }}
        />
      )}

      {/* Recipes Modal */}
      {showRecipesModal && (
        <Recipes
          onClose={() => setShowRecipesModal(false)}
          dataLoaded={dataLoaded}
          onRecipeApplied={async (result) => {
            setShowRecipesModal(false);
            const entries = await loadData();
            const last = Array.isArray(entries) ? entries[entries.length - 1] : null;
            toast.success(
              result.localizedMessage
              || (last?.code ? t(last.code, last.params) : null)
              || result.message
            );
          }}
        />
      )}

      {/* Full-page loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="card p-6 flex flex-col items-center gap-4 shadow-lg">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{t('app.processing')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
